import type { CSSProperties } from 'react'
import {
  CircleAlert, CircleDot, Check, FileWarning, TriangleAlert, X,
} from 'lucide-react'
import type { Blok } from '../lib/types'
import Film from './Film'
import Kijk from './Kijk'
import Plaat from './Plaat'
import Sorteer from './Sorteer'
import Stroom from './Stroom'
import Spot from './Spot'
import Keuze from './Keuze'
import Bouwer from './Bouwer'

/* ===========================================================================
 *  Een blok op het scherm zetten
 *
 *  Eén plek waar een soort uit de cursus een onderdeel wordt. Een nieuwe
 *  werkvorm toevoegen is: een soort erbij in lib/types.ts, een onderdeel
 *  erbij, en één regel hieronder. De cursus zelf blijft gewone tekst.
 *
 *  De werkvormen melden wanneer ze gedaan zijn (opKlaar). Het lesscherm telt
 *  die mee: pas als alle werkvormen in een module zijn gedaan, gaat de knop
 *  naar de volgende module open.
 *
 *  Waarom dat afgedwongen wordt, en dat is een keuze
 *  ------------------------------------------------
 *
 *  Omdat doorklikken anders het snelste pad is, en dan heeft de cursus niets
 *  gedaan behalve een vinkje opleveren. Het is ook veilig om af te dwingen:
 *  je kunt geen enkele werkvorm verliezen. Fout antwoorden mag, je krijgt de
 *  uitleg, en je gaat door. Wat hier vastzit is de aandacht, niet het cijfer.
 * =========================================================================== */

export default function Blokken({
  blokken, opKlaar,
}: {
  blokken: Blok[]
  /** Wordt aangeroepen met de index van de werkvorm die net is afgerond. */
  opKlaar: (index: number) => void
}) {
  return (
    <div className="blokken">
      {blokken.map((blok, i) => (
        <div
          key={i}
          className="opkomst"
          style={{ '--vertraging': `${Math.min(i * 50, 300)}ms` } as CSSProperties}
        >
          <EenBlok blok={blok} opKlaar={() => opKlaar(i)} />
        </div>
      ))}
    </div>
  )
}

/**
 * Welke blokken een handeling van de cursist vragen.
 *
 * De film staat er bewust NIET bij, al is hij wel aan te klikken. Een film
 * die af moet voordat je verder mag, is een film waar je bij vastzit als het
 * afspelen hapert -- en dan is de cursus stuk op precies het toestel waar hij
 * het hardst nodig is. De spelletjes kun je niet verliezen; die mogen wel
 * verplicht zijn.
 */
export function isWerkvorm(blok: Blok): boolean {
  return blok.soort === 'sorteer' || blok.soort === 'spot'
    || blok.soort === 'keuze' || blok.soort === 'prompt'
    || blok.soort === 'kijk'
}

function EenBlok({ blok, opKlaar }: { blok: Blok; opKlaar: () => void }) {
  switch (blok.soort) {
    case 'tekst':
      return (
        <div className="blok-tekst">
          {blok.alineas.map((a, i) => <p key={i}>{a}</p>)}
        </div>
      )

    case 'kern':
      return (
        <div className="kaart">
          {blok.titel && <h3><CircleDot size={17} style={{ color: 'var(--text-brand)' }} />{blok.titel}</h3>}
          <ul className="lijst">
            {blok.punten.map((p, i) => (
              <li key={i}><Check size={16} />{p}</li>
            ))}
          </ul>
        </div>
      )

    case 'let-op':
      return (
        <div className="roep let-op gevaar-streep">
          <TriangleAlert size={19} />
          <span>{blok.tekst}</span>
        </div>
      )

    case 'concept':
      return (
        <div className="roep concept gevaar-streep">
          <FileWarning size={19} />
          <span>
            <span className="titel">Nog niet vastgesteld</span>
            {blok.tekst}
          </span>
        </div>
      )

    case 'vergelijk':
      return (
        <div className="vergelijk">
          <div className="kolom niet">
            <h4><CircleAlert size={15} />{blok.nietTitel}</h4>
            <ul className="lijst">
              {blok.niet.map((p, i) => <li key={i}><X size={15} />{p}</li>)}
            </ul>
          </div>
          <div className="kolom wel">
            <h4><Check size={15} />{blok.welTitel}</h4>
            <ul className="lijst">
              {blok.wel.map((p, i) => <li key={i}><Check size={15} />{p}</li>)}
            </ul>
          </div>
        </div>
      )

    case 'tijdlijn':
      return (
        <div className="tijdlijn">
          {blok.punten.map((punt, i) => (
            <div
              key={i}
              className={`punt opkomst ${punt.vast ? '' : 'los'}`}
              style={{ '--vertraging': `${i * 80}ms` } as CSSProperties}
            >
              <div className="wanneer">{punt.wanneer}</div>
              <div className="wat">{punt.wat}</div>
              {punt.toelichting && <div className="toelichting">{punt.toelichting}</div>}
              {!punt.vast && (
                <div className="twijfel">
                  <TriangleAlert size={12} /> nakijken voor gebruik
                </div>
              )}
            </div>
          ))}
        </div>
      )

    case 'film':    return <Film blok={blok} opKlaar={opKlaar} />
    case 'stroom':  return <Stroom blok={blok} />
    case 'plaat':   return <Plaat blok={blok} />
    case 'kijk':    return <Kijk blok={blok} opKlaar={opKlaar} />
    case 'sorteer': return <Sorteer blok={blok} opKlaar={opKlaar} />
    case 'spot':    return <Spot blok={blok} opKlaar={opKlaar} />
    case 'keuze':   return <Keuze blok={blok} opKlaar={opKlaar} />
    case 'prompt':  return <Bouwer blok={blok} opKlaar={opKlaar} />
  }
}
